import BpmnJS from "bpmn-js";

// Converts BPMN diagrams to standalone image blobs, inlining the host theme's
// CSS custom property values so the exported SVG renders correctly outside the
// document (e.g. as a preview <img>, where document-level CSS does not cascade).

/**
 * CSS custom properties referenced by the StekzRenderer. Their values must be
 * inlined into exported SVGs because referencing `var(--…)` from inside an
 * <img>-rendered SVG cannot resolve against the host document and would fall
 * back to the property's initial value (e.g. fill: black).
 */
const EXPORTED_CSS_VARIABLES = [
    "--p-content-background",
    "--text-color",
    "--stekz-primary-color",
] as const;

/**
 * Reads the resolved values of the renderer's CSS custom properties from the
 * current document and embeds them in a <style> block at the top of the SVG.
 * The embedded rule targets the root <svg> element, so the variables cascade
 * to all descendants and resolve even when the SVG is rendered in isolation.
 */
function inlineThemeVariables(svg: string): string {
    const computed = getComputedStyle(document.documentElement);
    const declarations = EXPORTED_CSS_VARIABLES.map((name) => {
        const value = computed.getPropertyValue(name).trim();
        return value ? `${name}:${value};` : "";
    })
        .filter(Boolean)
        .join("");
    if (!declarations) return svg;
    const styleBlock = `<style>svg{${declarations}}</style>`;
    return svg.replace(/<svg\b[^>]*>/, (match) => `${match}${styleBlock}`);
}

/**
 * Converts a BPMN file to an image.
 * @param bpmnJS - The BpmnJS instance to use for conversion.
 * @param type - The desired output type: 'png', 'svg', or 'jpeg'.
 * @returns A Promise resolving to the image data blob
 */
export async function bpmnToImage(
    bpmnJS: BpmnJS,
    type: "png" | "svg" | "jpeg"
): Promise<Blob | null> {
    try {
        const { svg: rawSvg } = await bpmnJS.saveSVG();
        const svg = inlineThemeVariables(rawSvg);
        if (type === "svg") {
            return new Blob([svg], { type: "image/svg+xml" });
        } else {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d")!;

            const img = new Image();
            return new Promise((resolve, reject) => {
                img.onload = () => {
                    canvas.width = img.width;
                    canvas.height = img.height;
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

                    // Export as PNG or JPEG
                    canvas.toBlob(function (blob) {
                        resolve(blob);
                    }, `image/${type}`);
                };

                img.onerror = reject;
                img.src = `data:image/svg+xml;base64,${btoa(svg)}`;
            });
        }
    } catch (error) {
        console.error("Error converting BPMN to image:", error);
        throw new Error("Failed to convert BPMN to image.", { cause: error });
    }
}
